import {Box, Container, Text} from '@chakra-ui/react';
import {useMutation} from '@tanstack/react-query';
import {sendReview} from 'api/requests';
import {useFormik} from 'formik';
import {useToast} from 'hooks';
import {useRouter} from 'next/router';
import {FC} from 'react';
import {TranslationKeys} from 'translations';
import {IOrder, ISendReviewRequest, ISendReviewResponse} from 'types';
import logger from 'utils/logger';

import {RateForm} from './components/grade-form';

interface IProps {
  t: (term: TranslationKeys) => string;
  order: IOrder;
}

export const OrderReview: FC<IProps> = ({t, order}) => {
  const toast = useToast();
  const {push} = useRouter();
  const {values, setFieldValue, submitForm} = useFormik({
    initialValues: {
      courier_rate: undefined,
      order_rate: undefined,
      comment: '',
      comment_courier: '',
      imageObj: undefined,
    },
    onSubmit: (values) => {
      const review: ISendReviewRequest = {
        comment: values.comment,
        comment_courier: values.comment_courier,
        order_rate: values.order_rate ?? 5,
        courier_rate: values.courier_rate ?? 5,
      };

      let formData = new FormData();
      if (values.imageObj) {
        formData = new FormData();
        formData.append('image', values.imageObj);
        formData.append('data', JSON.stringify(review));
      }

      const data: ISendReviewRequest | FormData = values.imageObj ? formData : review;
      mutationSendReview.mutate(data);
    },
  });

  const mutationSendReview = useMutation<ISendReviewResponse, any, ISendReviewRequest | FormData, any>(
    async (review) => {
      return await sendReview(order.uuid, review);
    },
    {
      onError: (error) => {
        toast.error(t('ERROR_SEND_REVIEW'));
        const textErrorArr = JSON.stringify(error?.response?.data);

        toast.error(textErrorArr);
        logger.error(error);
      },
      onSuccess: () => {
        push(`/`);
        toast.success(t('SUCCESS_SEND_REVIEW'));
      },
    },
  );

  const onChangeCourierGrade = (grade: number, comment?: string) => {
    setFieldValue('courier_rate', grade);
    setFieldValue('comment_courier', comment);
  };

  const onChangeOrderGrade = (grade: number, comment?: string) => {
    setFieldValue('order_rate', grade);
    setFieldValue('comment', comment);

    submitForm();
  };

  const onImageUpload = (imageObj: any) => {
    setFieldValue('imageObj', imageObj);
  };

  return (
    <>
      <Text as="h3" fontSize={['18px', '28px']} fontWeight="700" mb="0px">
        {t('EVALUATE_ORDER')}
      </Text>
      <Container
        variant="white-block"
        display="flex"
        justifyContent="center"
        alignItems="center"
        p="30px"
        mt="0px"
        position="relative"
      >
        <Box>
          {values.courier_rate ? (
            <RateForm
              key={values.courier_rate}
              title={t('EVALUATE_ORDER_RATE_FORM_TITLE')}
              name={order.provider.name ?? 'Straus Provider'}
              onChangeGrade={onChangeOrderGrade}
              t={t}
              titleImage={order.provider.logo_thumbnail ?? '/food_pattern.webp'}
              isDisabledForm={mutationSendReview.isLoading}
              onImageUpload={onImageUpload}
              imageObj={values.imageObj}
            />
          ) : (
            <RateForm
              title={t('EVALUATE_COURIER_RATE_FORM_TITLE')}
              name={order.courier?.first_name ?? 'Straus Courier'}
              onChangeGrade={onChangeCourierGrade}
              t={t}
              titleImage={order.courier?.photo ?? '/courier-default-photo.svg'}
              isDisabledForm={mutationSendReview.isLoading}
            />
          )}
        </Box>
      </Container>
    </>
  );
};
